import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import getHotelEncryptedMargin from 'loyalty/lib/margin/getHotelEncryptedMargin'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { getRoomTypeCapacity } from 'lib/tours/tourUtils'
import getLoyaltyEarnKey from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'

interface BaseAdditionalOptions {
  skip?: boolean
  loyaltyProductType?: App.LoyaltyDefaultPointsEarnCalculationRequest['loyaltyProductType']
}

interface BasePricingOptions {
  price?: number
  memberPrice?: number
  promoKey?: string
}

interface GenerateLoyaltyPointsCalculatorHotelAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  currentRegionCode: string
  packageView?: App.OfferPackageOptionView
  pkg?: App.HotelPackage
  nights?: number
}

export function generateLoyaltyPointsCalculatorHotelOptions(
  offer: App.Offer | App.OfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorHotelAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    packageView,
    pkg,
    skip,
    price,
    memberPrice,
    currentRegionCode,
    nights,
  } = options
  const loyaltyProductType = getLoyaltyProductType(offer)

  if (
    !offer ||
    (!packageView && !pkg) ||
    (!loyaltyProductType || (loyaltyProductType !== 'hotel_flash' && loyaltyProductType !== 'hotel_lpc')) ||
    skip
  ) return

  const packageViewPrice = packageView?.pricing?.price ?? 0
  const packageViewMemberPrice = packageView?.memberPricing?.price ?? 0

  const packageViewPropertyFees = packageView?.pricing?.fees?.find((fee) => fee.type === 'property')?.amount ?? 0

  const packageViewPriceExcludingPropertyFees = packageViewPrice - packageViewPropertyFees
  const packageViewMemberPriceExcludingPropertyFees = packageViewMemberPrice > 0 ? packageViewMemberPrice - packageViewPropertyFees : 0
  const propertyCountryCode = offer.property?.geoData?.countryCode
  const propertyState = offer.property?.geoData?.administrativeAreaLevel1

  const isUltraLux = !!offer.property?.isUltraLux

  let earnKey: string | undefined

  if (loyaltyProductType === 'hotel_lpc') {
    earnKey = getLoyaltyEarnKey({ loyaltyProductType, options: { propertyCountryCode, propertyState, currentRegionCode, isUltraLux } })
  } else if (loyaltyProductType === 'hotel_flash') {
    earnKey = getLoyaltyEarnKey({ loyaltyProductType, options: { propertyCountryCode, propertyState, currentRegionCode } })
  }

  return {
    price: price ?? packageViewPriceExcludingPropertyFees,
    memberPrice: memberPrice ?? packageViewMemberPriceExcludingPropertyFees,
    loyaltyProductType,
    mx: getHotelEncryptedMargin(offer.type, packageView?.package ?? pkg),
    offerId: offer.id,
    packageId: packageView?.package.id ?? pkg?.id ?? '',
    earnKey,
    nights,
  }
}

interface GenerateLoyaltyPointsCalculatorTourAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

interface GenerateLoyaltyPointsCalculatorBedbankAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  propertyFees: number
  propertyId: string
  offerId: string
  propertyCountryCode: string
  propertyState: string | undefined
  currentRegionCode: string
  nights?: number
}

interface GenerateLoyaltyPointsCalculatorPropertyAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  propertyCountryCode?: string
  propertyState?: string
  currentRegionCode: string
  isUltraLux: boolean
  nights?: number
}

export function getLoyaltyBedbankPackageId(propertyId: string | undefined, supplierCode: string | undefined) {
  if (!propertyId || !supplierCode) return ''
  return `${propertyId}:${supplierCode}`
}

export function generateLoyaltyPointsCalculatorBedbankOptions(
  rate: App.BedbankRate | undefined,
  options: GenerateLoyaltyPointsCalculatorBedbankAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price = 0,
    memberPrice = 0,
    propertyFees,
    propertyId,
    offerId,
    propertyCountryCode,
    propertyState,
    currentRegionCode,
    nights,
  } = options

  if (skip) return

  return {
    price: price - propertyFees,
    memberPrice: memberPrice > 0 ? memberPrice - propertyFees : 0,
    loyaltyProductType: 'hotel_lpp_eps',
    mx: rate?.mx,
    packageId: getLoyaltyBedbankPackageId(propertyId, rate?.supplierCode),
    offerId,
    earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { propertyCountryCode, propertyState, currentRegionCode } }),
    nights,
  }
}

export function generateLoyaltyPointsCalculatorPropertyOptions(
  rate: App.EnrichedPropertyRate,
  options: GenerateLoyaltyPointsCalculatorPropertyAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest {
  const baseRequest = {
    price: rate.totals.societeHotelPrice,
    memberPrice: rate.luxPlusTotals?.societeHotelPrice ?? 0,
    // TODO(Xiao): `mx` is not included on luxPlus???
    mx: rate.totals.mx,
    isUltraLux: options.isUltraLux,
    nights: options.nights,
  }
  const { propertyCountryCode, propertyState, currentRegionCode } = options

  if (rate.service.type === 'lpc') {
    return {
      ...baseRequest,
      loyaltyProductType: 'hotel_lpc',
      offerId: rate.service.offerId,
      packageId: rate.service.packageId,
      earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpc', options: { propertyCountryCode, propertyState, currentRegionCode, isUltraLux: options.isUltraLux } }),
    }
  } else if (rate.service.type === 'bedbank') {
    return {
      ...baseRequest,
      loyaltyProductType: 'hotel_lpp_eps',
      packageId: getLoyaltyBedbankPackageId(rate.service.propertyId, rate.service.supplierCode),
      offerId: rate.service.propertyId,
      earnKey: getLoyaltyEarnKey({ loyaltyProductType: 'hotel_lpp_eps', options: { propertyCountryCode, propertyState, currentRegionCode } }),
    }
  } else {
    assertUnreachable(rate.service)
  }
}

interface GenerateLoyaltyPointsCalculatorVillasAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  pkg: App.HotelPackage | undefined
  propertyFees: number
  nights?: number
}

export function generateLoyaltyPointsCalculatorVillasOptions(
  offer: App.VillaOffer | App.VillaOfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorVillasAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    pkg,
    skip,
    price = 0,
    memberPrice = 0,
    propertyFees,
    nights,
  } = options

  if (skip || !offer || !pkg) return

  const priceExcludingPropertyFees = price - propertyFees
  const memberPriceExcludingPropertyFees = memberPrice > 0 ? memberPrice - propertyFees : 0

  return {
    price: priceExcludingPropertyFees,
    memberPrice: memberPriceExcludingPropertyFees,
    loyaltyProductType: 'rental',
    mx: pkg.roomRate.mx,
    offerId: offer.id,
    packageId: pkg.id,
    nights,
  }
}

interface FlightPricingOptions {
  flightPrice?: number
}

interface GenerateLoyaltyPointsCalculatorFlightAdditionalOptions extends BaseAdditionalOptions, FlightPricingOptions {
  packageView?: App.OfferPackageOptionView
  loyaltyEarnKey?: string
  /** When true the booking is a Classic Rewards redemption — points cover the flight,
   *  cash co-pay is not earnable, and no flight earn request is generated. Hotel earn
   *  is computed by a separate generator and remains unaffected. */
  isReward?: boolean
}

export function generateLoyaltyPointsCalculatorBundledFlightOptions(
  offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorFlightAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    packageView,
    flightPrice,
    loyaltyEarnKey,
    isReward,
  } = options ?? {}

  if (
    (isBedbankOffer(offer) && !offer.flights.flightsEnabled) ||
    (!isBedbankOffer(offer) && !offer?.offerFlightsEnabled) ||
    skip ||
    isReward
  ) {
    return
  }

  return {
    price: flightPrice ?? packageView?.flightPrice ?? 0,
    loyaltyProductType: 'dom_economy',
    offerId: offer.id,
    packageId: '',
    earnKey: loyaltyEarnKey,
  }
}

export function generateLoyaltyPointsCalculatorPropertyBundledFlightOptions(
  rate: App.EnrichedPropertyRate,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  if (!(rate.includesFlights && typeof rate.cheapestFlightPrice === 'number')) return
  return {
    price: rate.cheapestFlightPrice,
    loyaltyProductType: 'dom_economy',
    offerId: '',
    packageId: '',
  }
}

interface GenerateLoyaltyPointsCalculatorInsuranceAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLoyaltyPointsCalculatorInsuranceOptions(
  options?: GenerateLoyaltyPointsCalculatorInsuranceAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
    memberPrice,
    loyaltyProductType,
    promoKey,
  } = options ?? {}

  if (skip) return

  return {
    price: price ?? 0,
    memberPrice: memberPrice ?? 0,
    loyaltyProductType: loyaltyProductType ?? 'insurance',
    promoKey: promoKey,
  }
}

interface GenerateLoyaltyPointsCalculatorExperienceAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLoyaltyPointsCalculatorExperienceOptions(
  offer: App.ExperienceOffer | undefined,
  options: GenerateLoyaltyPointsCalculatorExperienceAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
    memberPrice,
  } = options ?? {}

  if (skip) return

  return {
    price: price ?? offer?.price ?? 0,
    memberPrice: memberPrice ?? 0,
    offerId: offer?.id,
    loyaltyProductType: 'experience',
  }
}

interface GenerateLoyaltyPointsCalculatorTourAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  roomType?: App.Tours.V2OfferRoomType
  packageId?: string
  offerId?: string
  nights?: number
}

export function generateLoyaltyPointsCalculatorTourOptions(
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorTourAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
    memberPrice,
    roomType,
    packageId,
    offerId,
    nights,
  } = options
  if (
    !offer ||
    skip
  ) return

  const loyaltyProductType = getLoyaltyProductType(offer)

  if (!loyaltyProductType) return

  return {
    price: (price ?? 0) * getRoomTypeCapacity(roomType),
    memberPrice: (memberPrice ?? 0) * getRoomTypeCapacity(roomType),
    loyaltyProductType,
    packageId: packageId ?? '',
    offerId: offerId ?? '',
    nights,
  }
}

interface GenerateLoyaltyPointsCalculatorTourExperienceAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  count: number
}

export function generateLoyaltyPointsCalculatorTourExperienceOptions(
  tourExperience: App.Tours.TourExperience,
  options?: GenerateLoyaltyPointsCalculatorTourExperienceAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    count,
  } = options ?? {}

  if (skip) return

  return {
    price: tourExperience.price * (count ?? 1),
    loyaltyProductType: 'tour_optional_extra',
  }
}

interface GenerateLoyaltyPointsCalculatorCarHireAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  selectedInsurance?: number
  earnKey?: string
}

export function generateLoyaltyPointsCalculatorCarHireOptions(
  offer: App.CarHireOffer | App.CarHireOfferSummary | undefined,
  options: GenerateLoyaltyPointsCalculatorCarHireAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const { skip, memberPrice, earnKey } = options ?? {}

  if (skip) return

  let price = 0

  // At the moment only apply loyalty points to the pay now amount
  if (offer && 'rateOptions' in offer && offer.rateOptions.length) {
    price = offer.rateOptions.reduce((acc, rateOption) => {
      return acc + (rateOption.payNowAmount || 0)
    }, 0)
  } else if (offer && 'totalCharge' in offer) {
    price = offer.totalCharge.payNowAmount
  }

  return {
    price: (price ?? 0) + (options.selectedInsurance ?? 0),
    memberPrice: memberPrice ?? 0,
    loyaltyProductType: 'car_hire',
    earnKey,
  }
}

interface GenerateLoyaltyPointsCalculatorCruiseAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  bookingPrice?: number
  nights?: number
}

export function generateLoyaltyPointsCalculatorCruiseOptions(
  offer: App.CruiseOffer | undefined,
  options?: GenerateLoyaltyPointsCalculatorCruiseAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    bookingPrice,
    nights,
  } = options ?? {}

  const loyaltyProductType = getLoyaltyProductType(offer)

  if (skip || !loyaltyProductType || !offer) return

  const price = bookingPrice || offer.mainDepartureDetails.lowestOverallPriceDetails.total || 0

  return {
    price,
    loyaltyProductType,
    packageId: offer.mainDepartureDetails.id,
    offerId: offer.id,
    nights: nights ?? offer.duration,
    earnKey: offer.earnKey,
  }
}

interface GenerateLoyaltyPointsCalculatorTransfersAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {
  offerId?: string
}

export function generateLoyaltyPointsCalculatorTransfersOptions(
  options?: GenerateLoyaltyPointsCalculatorTransfersAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
    memberPrice,
    offerId,
  } = options ?? {}

  if (skip) return

  return {
    price: price ?? 0,
    memberPrice: memberPrice ?? 0,
    loyaltyProductType: 'experience',
    offerId,
  }
}

interface GenerateLoyaltyPointsCalculatorMerchandiseAdditionalOptions extends BaseAdditionalOptions, BasePricingOptions {}

export function generateLoyaltyPointsCalculatorMerchandiseOptions(
  variant: App.MerchandiseOfferVariant | undefined,
  options: GenerateLoyaltyPointsCalculatorMerchandiseAdditionalOptions,
): App.LoyaltyPointsEarnCalculationRequest | undefined {
  const {
    skip,
    price,
  } = options ?? {}

  if (!variant || skip || !price) return

  return {
    price,
    loyaltyProductType: variant.loyalty.productType,
    earnKey: variant.loyalty.earnKey,
  }
}
